import HNSColors from "../../../ui library/HNSColors";

function nullCheck(obj) {
  return !obj || (Array.isArray(obj?.data) && obj?.data.length === 0);
}

function normalizeChartRecord(record) {
  if (nullCheck(record) || (record?.raw && nullCheck(record?.raw))) {
    return null;
  }

  return record;
}

function getDataArray(raw) {
  const arr = raw?.data;
  return Array.isArray(arr) ? arr : [];
}

function getLabelKey(dimensions) {
  return dimensions?.[0]?.data_key ?? null;
}

function getValueKey(measures) {
  return measures?.[0]?.data_key ?? "cnt";
}

function formatNumber(value) {
  const num = Number(value);
  return Number.isNaN(num) ? "--" : num.toLocaleString();
}

function formatTrendValue(value, format) {
  if (value === null || value === undefined) {
    return "--";
  }

  if (format === "trend") {
    const num = Number(value);
    if (Number.isNaN(num)) {
      return "--";
    }
    const percent = Math.abs(num) <= 1 ? num * 100 : num;
    const sign = percent > 0 ? "+" : "";
    return `${sign}${percent.toFixed(1)}%`;
  }

  if (format === "currency") {
    const num = Number(value);
    if (Number.isNaN(num)) {
      return String(value);
    }
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(num);
  }

  if (format === "number") {
    const num = Number(value);
    return Number.isNaN(num) ? String(value) : num.toLocaleString();
  }

  return String(value);
}

function getTrendDirection(trendValue) {
  const num = Number(trendValue);
  if (Number.isNaN(num)) {
    return "flat";
  }
  const percent = Math.abs(num) <= 1 ? num * 100 : num;
  if (Math.abs(percent) < 1) {
    return "flat";
  }
  return percent > 0 ? "up" : "down";
}

function getTrendColor(direction) {
  if (direction === "up") {
    return HNSColors.success[500];
  }
  if (direction === "down") {
    return HNSColors.failure[500];
  }
  return HNSColors.neutrals[400];
}

function getMeasureFormat(measures, dataKey) {
  const measure = (measures ?? []).find((x) => x?.data_key === dataKey);
  return measure?.format ?? "number";
}

function getFirstRow(raw) {
  const arr = raw?.data;
  if (Array.isArray(arr) && arr.length > 0) {
    return arr[0];
  }
  return null;
}

function getMetaAndData(chart) {
  const title = chart?.meta?.title ?? "";
  const description = chart?.meta?.description ?? "";
  const dimensions = chart?.dimensions ?? [];
  const measures = chart?.measures ?? [];
  const dataUrl = chart?.data ?? "";
  const filtersUrl = chart?.filters ?? "";

  return { title, description, dimensions, measures, dataUrl, filtersUrl };
}

function getChartColor(elementIndex) {
  const COLORS = [
    HNSColors.secondary[400],
    HNSColors.secondary[500],
    HNSColors.secondary[600],
    HNSColors.secondary[800],
    HNSColors.secondary[900],
    HNSColors.secondary[950],
  ];
  if (!Number.isInteger(elementIndex)) {
    return COLORS[0];
  }
  return COLORS[elementIndex % COLORS.length];
}

/**
 * Prepares and transforms chart data for visualization.
 *
 * - If there are no dimensions but measures and data exist, it restructures the data for single-row charts.
 * - If there are more than 10 data rows and at least one dimension, it aggregates all but the top 9 rows (by sum of measure values) into an "Others" category.
 *
 * @param {Object} chart - The chart configuration object.
 * @param {Array<Object>} [chart.dimensions=[]] - Array of dimension objects, each with a `data_key`.
 * @param {Array<Object>} [chart.measures=[]] - Array of measure objects, each with a `data_key` and optional `label`.
 * @param {Array<Object>} [chart.data=[]] - Array of data rows, each an object keyed by dimension and measure data keys.
 * @returns {{ data: Array<Object>, dimensions: Array<Object>, measures: Array<Object> }} The transformed chart data, dimensions, and measures.
 */
function prepareChartData(chart) {
  let { dimensions = [], measures = [], data = [] } = chart || {};
  if (dimensions.length === 0 && measures.length > 0 && data.length > 0) {
    // the assumtion is that the data will always have a single row if there are no dimensions
    data = measures.map((measure) => ({
      name: measure?.label,
      value: data[0]?.[measure?.data_key],
    }));
    dimensions = [{ data_key: "name" }];
    measures = [{ data_key: "value" }];
  }
  const DATA_CAP = dimensions[0]?.limit || 8;
  if (Array.isArray(data) && data.length > DATA_CAP && dimensions.length > 0) {
    if (chart?.options?.limit_strategy === "preserve_order") {
      data = data.slice(0, DATA_CAP);
      return { data, dimensions, measures };
    }

    const TOP_LIMIT = DATA_CAP - 1;
    const dimKey = dimensions[0]?.data_key;
    const measureKeys = measures.map((m) => m.data_key);

    const withSums = data.map((row, idx) => {
      const sum = measureKeys.reduce((acc, key) => acc + Math.abs(Number(row[key]) || 0), 0);
      return { row, sum, idx };
    });

    withSums.sort((a, b) => b.sum - a.sum || a.idx - b.idx);

    const top = withSums.slice(0, TOP_LIMIT).map((x) => x.row);

    const others = withSums.slice(TOP_LIMIT).map((x) => x.row);
    if (others.length > 0) {
      const collapsed = {};
      collapsed[dimKey] = "Others";
      for (const key of measureKeys) {
        collapsed[key] = others.reduce((acc, row) => acc + (Number(row[key]) || 0), 0);
      }
      data = [...top, collapsed];
    } else {
      data = top;
    }
  }
  return { data, dimensions, measures };
}

export {
  formatNumber,
  formatTrendValue,
  getDataArray,
  getFirstRow,
  getLabelKey,
  getMeasureFormat,
  getMetaAndData,
  getTrendColor,
  getTrendDirection,
  getValueKey,
  normalizeChartRecord,
  getChartColor,
  prepareChartData,
};
